import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  CopyButton,
  ExternalLink,
  NetworkIcon,
  None,
  PageHeader,
  QueryError,
  SkeletonBlock,
  cx,
  formatRelative,
  formatUsd,
  shortHash,
  useDocumentTitle,
} from "@unizpay/ui";
import { RefreshCw } from "lucide-react";
import { type RelayerBalance, api } from "../lib/api";

/** "0.042100000000000000" → "0.0421": at most 6 decimals, grouped thousands. */
function formatCoin(value: string): string {
  const [whole = "0", frac = ""] = value.split(".");
  const shown = frac.slice(0, 6).replace(/0+$/, "");
  const grouped = BigInt(whole).toLocaleString("en-US");
  if (shown) return `${grouped}.${shown}`;
  // Dust below the shown precision is still not empty.
  return /[1-9]/.test(frac) ? `<${grouped}.000001` : grouped;
}

type Status = { label: string; tone: "ok" | "warn" | "danger" | "muted"; detail?: string };

function statusOf(r: RelayerBalance): Status {
  if (!r.address) return { label: "Not set up", tone: "muted", detail: r.error ?? undefined };
  if (r.error) return { label: "Couldn't read", tone: "danger", detail: r.error };
  if (r.low) return { label: "Low", tone: "warn", detail: "Refill this relayer" };
  if (r.balance !== null && /^0(\.0*)?$/.test(r.balance)) {
    return { label: "Empty", tone: r.enabled ? "danger" : "muted" };
  }
  return { label: "OK", tone: "ok" };
}

const TONES: Record<Status["tone"], string> = {
  ok: "bg-ok-subtle text-ok",
  warn: "bg-warn-subtle text-warn",
  danger: "bg-danger-subtle text-danger",
  muted: "bg-subtle text-fg-muted",
};

function StatusPill({ status }: { status: Status }) {
  return (
    <span
      title={status.detail}
      className={cx(
        "inline-flex rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        TONES[status.tone],
      )}
    >
      {status.label}
    </span>
  );
}

function Row({ r }: { r: RelayerBalance }) {
  const status = statusOf(r);
  return (
    <li className="grid gap-x-4 gap-y-2 px-4 py-3 sm:px-5 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto] md:items-center">
      <div className="flex min-w-0 items-center gap-2.5">
        <NetworkIcon network={r.chain_id} size={24} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{r.name}</p>
          <p className="text-xs text-fg-muted">{r.enabled ? "Enabled" : "Not enabled"}</p>
        </div>
      </div>

      <div className="min-w-0 text-[0.8125rem]">
        {r.address ? (
          <span className="flex min-w-0 items-center gap-1.5">
            {r.explorer_url ? (
              <ExternalLink href={r.explorer_url} className="font-mono">
                {shortHash(r.address, 8, 6)}
              </ExternalLink>
            ) : (
              <span className="font-mono">{shortHash(r.address, 8, 6)}</span>
            )}
            <CopyButton value={r.address} label={`Copy the ${r.name} relayer address`} compact />
          </span>
        ) : (
          <None />
        )}
      </div>

      <div className="text-sm md:text-right">
        {r.balance !== null ? (
          <>
            <p className="font-medium tabular-nums">
              {formatCoin(r.balance)} <span className="text-fg-muted">{r.symbol}</span>
            </p>
            {r.usd !== null && <p className="text-xs text-fg-muted tabular-nums">{formatUsd(r.usd)}</p>}
            {r.resources && (
              <p className="text-xs text-fg-muted tabular-nums">
                {BigInt(r.resources.energy).toLocaleString("en-US")} energy ·{" "}
                {BigInt(r.resources.bandwidth).toLocaleString("en-US")} bandwidth
              </p>
            )}
          </>
        ) : (
          <None />
        )}
      </div>

      <div className="flex items-center gap-2 md:justify-end">
        <StatusPill status={status} />
        {status.tone === "danger" && status.detail && (
          <span className="text-xs text-fg-muted md:hidden">{status.detail}</span>
        )}
      </div>
    </li>
  );
}

function Group({ title, description, rows }: { title: string; description: string; rows: RelayerBalance[] }) {
  // Networks this deployment serves first: those are the ones that must stay funded.
  const sorted = [...rows].sort((a, b) => Number(b.enabled) - Number(a.enabled));
  return (
    <Card title={title} description={description} flush>
      <ul className="divide-y divide-line">
        {sorted.map((r) => (
          <Row key={r.chain_id} r={r} />
        ))}
      </ul>
    </Card>
  );
}

export function RelayersPage() {
  useDocumentTitle("Relayers");
  const q = useQuery({
    queryKey: ["relayers"],
    queryFn: api.relayers,
    // Live chain reads: refresh every minute while the page is open.
    refetchInterval: 60_000,
  });

  const rows = q.data?.networks ?? [];
  const needsAttention = rows.filter((r) => r.enabled && (statusOf(r).tone === "danger" || r.low));

  return (
    <>
      <PageHeader
        title="Relayers"
        description="Gas balances of the wallets that settle payments, on every network. EVM networks share one relayer address."
        actions={
          <>
            {q.data && (
              <span className="text-xs text-fg-muted">Checked {formatRelative(q.data.checked_at)}</span>
            )}
            <Button variant="secondary" size="sm" onClick={() => void q.refetch()} loading={q.isFetching}>
              <RefreshCw aria-hidden="true" /> Refresh
            </Button>
          </>
        }
      />

      {q.isPending ? (
        <SkeletonBlock lines={8} />
      ) : q.isError ? (
        <QueryError
          error={q.error}
          title="Couldn't load the relayer balances"
          onRetry={() => void q.refetch()}
          retrying={q.isFetching}
        />
      ) : (
        <div className="space-y-6">
          {needsAttention.length > 0 && (
            <Alert
              tone="warn"
              title={
                needsAttention.length === 1
                  ? "1 enabled network needs attention"
                  : `${needsAttention.length} enabled networks need attention`
              }
            >
              {needsAttention.map((r) => `${r.name} (${statusOf(r).label.toLowerCase()})`).join(", ")}
            </Alert>
          )}
          <Group
            title="Mainnets"
            description="Live payments. An enabled network's relayer under $10 is shown as low."
            rows={rows.filter((r) => !r.testnet)}
          />
          <Group
            title="Testnets"
            description="Test mode. Balances only, no dollar value."
            rows={rows.filter((r) => r.testnet)}
          />
        </div>
      )}
    </>
  );
}
